import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  ContextBubble,
  ContextBubbleAnimation,
} from '@homex/se-react-components';
import { CarouselItemId } from '@homex/se-widget-flow-types';
import { loader } from 'graphql.macro';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components/macro';

import { setCurrentCarouselItem, setServiceAddress } from 'actions';
import { CarouselBottomButton } from 'components/CarouselBottomButton/CarouselBottomButton';
import { Phrase } from 'components/Phrase';
import { RadioButton } from 'components/RadioButton';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
  CarouselReduxReducer,
  ICarouselItemAddressCorrectionBottomProps,
  IState,
  ServiceType,
} from 'typings';
import {
  ErrorCode,
  IAddressInput,
  IGetAddressBookingStatusForCustomerQuery,
  IGetAddressBookingStatusForCustomerQueryVariables,
  IGetCorrectedAddressesQuery,
  IGetCorrectedAddressesQueryVariables,
  IRegisterAddressMutation,
  IRegisterAddressMutationVariables,
} from 'typings/GraphQL';

import {
  Buttons,
  Container,
  ContextBubbleCustom,
  CorrectedAddresses,
  Fieldset,
  FieldsetMutedContainer,
  FormGroup,
  Legend,
} from './styles';

const getAddressBookingStatusForCustomerQuery = loader(
  '../../queries/getAddressBookingStatusForCustomer.graphql',
);

const getCorrectedAddressesQuery = loader(
  '../../queries/getCorrectedAddresses.graphql',
);

const registerAddressMutation = loader('../../queries/registerAddress.graphql');

const RADIO_GROUP_NAME = 'CarouselItemAddressCorrectionRadioButton';
const RADIO_CUSTOM_VALUE = 'CUSTOM';
const CAROUSEL_ITEM_ID = CarouselItemId.RepairAddressCorrection;

export const CarouselItemAddressCorrectionBottom = ({
  destinationId = CarouselItemId.ScheduleEmergency,
}: ICarouselItemAddressCorrectionBottomProps) => {
  const dispatch = useThunkDispatch();
  const {
    city = '',
    flow,
    phoneNumber = '',
    serviceType = ServiceType.REPAIR,
    state = '',
    street1 = '',
    street2 = '',
    zipCode = '',
  } = useSelector((reduxState: IState) => ({
    city: reduxState.userInput.address.city,
    flow: reduxState.client.flow,
    phoneNumber: reduxState.userInput.customerPhone.number,
    serviceType: reduxState.userInput.serviceType,
    state: reduxState.userInput.address.state,
    street1: reduxState.userInput.address.street1,
    street2: reduxState.userInput.address.street2,
    zipCode: reduxState.userInput.address.zipCode,
  }));

  const { buttonType } = useTheme();

  const bottomButtonFormat = flow?.buttonType ?? buttonType;

  const [continueButtonErrorMessage, setContinueButtonErrorMessage] =
    useState<React.ReactNode>('');
  const [serviceableErrorMessage, setServiceableErrorMessage] =
    useState<React.ReactNode>('');
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [getCorrectedAddressesData, setGetCorrectedAddressData] =
    useState<IGetCorrectedAddressesQuery>();

  const finalDestinationItemId = destinationId;

  const selectAddressAndContinue = (address: IAddressInput) => {
    dispatch(setServiceAddress(address));

    finalDestinationItemId &&
      dispatch(
        setCurrentCarouselItem(
          CarouselReduxReducer.SERVICE,
          finalDestinationItemId,
        ),
      );
  };

  useQuery<IGetCorrectedAddressesQuery, IGetCorrectedAddressesQueryVariables>(
    getCorrectedAddressesQuery,
    {
      onCompleted(data) {
        if (!getCorrectedAddressesData) {
          setGetCorrectedAddressData(data);
        }
      },
      skip: !!getCorrectedAddressesData,
      variables: {
        address: {
          city,
          state,
          street1,
          street2,
          zipCode,
        },
      },
    },
  );

  const correctedAddresses =
    getCorrectedAddressesData?.getCorrectedAddresses?.correctedAddresses || [];
  const correctedAddress = correctedAddresses.find(
    ({ address: { id } }) => id === selectedAddressId,
  );

  const [
    getAddressBookingStatusForCustomer,
    { loading: isGetAddressBookingStatusForCustomerLoading },
  ] = useLazyQuery<
    IGetAddressBookingStatusForCustomerQuery,
    IGetAddressBookingStatusForCustomerQueryVariables
  >(getAddressBookingStatusForCustomerQuery, {
    fetchPolicy: 'network-only',
    onCompleted: (result: IGetAddressBookingStatusForCustomerQuery) => {
      const isServiceable =
        !!result.getAddressBookingStatusForCustomer?.isServiceable;

      if (isServiceable && correctedAddress?.address) {
        selectAddressAndContinue(correctedAddress.address);
      } else {
        setServiceableErrorMessage(Phrase({ id: 'SERVICE_AREA.ERROR.HTML' }));
      }
    },
    onError: () => {
      setContinueButtonErrorMessage(
        Phrase({ id: 'SALES_ADDRESS_CORRECTION.SERVER_ERROR' }),
      );
    },
  });

  const [
    registerAddress,
    { data: registerAddressData, loading: isRegisterAddressLoading },
  ] = useMutation<IRegisterAddressMutation, IRegisterAddressMutationVariables>(
    registerAddressMutation,
    {
      onCompleted: (result: IRegisterAddressMutation) => {
        const {
          address = {
            city: '',
            id: '',
            state: '',
            street1: '',
            street2: '',
            timezone: '',
            zipCode: '',
          },
          errorCode,
        } = result.registerAddress || {};

        const inputAddress = {
          city,
          state,
          street1,
          street2,
          zipCode,
        };

        if (!errorCode && address) {
          selectAddressAndContinue({ ...address, ...inputAddress });
        }
      },
      onError: () => {
        setContinueButtonErrorMessage(
          Phrase({ id: 'SALES_ADDRESS_CORRECTION.SERVER_ERROR' }),
        );
      },
    },
  );

  const isLoading =
    isGetAddressBookingStatusForCustomerLoading || isRegisterAddressLoading;
  const registerAddressErrorCode =
    registerAddressData?.registerAddress?.errorCode;

  const isCustomSelected = selectedAddressId === RADIO_CUSTOM_VALUE;
  const isCustomInvalid = !!(
    isCustomSelected &&
    registerAddressErrorCode === ErrorCode.ERROR_ADDRESS_INVALID
  );
  const isDisabled = !selectedAddressId || isCustomInvalid;
  const isCustomSelectionErrorVisible = isCustomSelected || isCustomInvalid;

  const handleRadioChange = (event: React.FormEvent<HTMLInputElement>) => {
    setSelectedAddressId(event.currentTarget.value);
    setServiceableErrorMessage('');
  };

  const handleContinueClick = async () => {
    setContinueButtonErrorMessage('');
    setServiceableErrorMessage('');

    if (correctedAddress && selectedAddressId !== RADIO_CUSTOM_VALUE) {
      const {
        address: {
          city: correctedCity,
          state: correctedState,
          street1: correctedStreet1,
          street2: correctedStreet2,
          zipCode: correctedZipCode,
        },
      } = correctedAddress;

      getAddressBookingStatusForCustomer({
        variables: {
          address: {
            city: correctedCity,
            state: correctedState,
            street1: correctedStreet1,
            street2: correctedStreet2,
            zipCode: correctedZipCode,
          },
          phoneNumber,
          serviceType,
        },
      });
    } else {
      registerAddress({
        variables: {
          address: {
            city,
            state,
            street1,
            street2,
            zipCode,
          },
        },
      });
    }
  };

  return (
    <FormGroup>
      <Container>
        <Fieldset>
          <Legend>
            {correctedAddresses.length === 1 ? (
              <Phrase id="SALES_ADDRESS_CORRECTION.SUGGESTED.TITLE" />
            ) : (
              <Phrase id="SALES_ADDRESS_CORRECTION.SUGGESTED.TITLE.PLURAL" />
            )}
          </Legend>
          <CorrectedAddresses>
            {correctedAddresses.map(({ address }) => {
              const isChecked = selectedAddressId === address.id;

              return (
                <React.Fragment key={address.id}>
                  <RadioButton
                    checked={isChecked}
                    name={RADIO_GROUP_NAME}
                    onChange={handleRadioChange}
                    value={address.id}
                  >
                    {address.street1} <br />
                    {address.street2 && (
                      <React.Fragment>
                        {address.street2} <br />
                      </React.Fragment>
                    )}
                    {`${address.city}, ${address.state} ${address.zipCode}`}
                  </RadioButton>
                  <ContextBubble
                    animation={ContextBubbleAnimation.FADE_IN_OUT}
                    isEnabled={isChecked && !!serviceableErrorMessage}
                    pointer={{
                      horizontal: 'center',
                      vertical: 'top',
                    }}
                  >
                    {serviceableErrorMessage}
                  </ContextBubble>
                </React.Fragment>
              );
            })}
          </CorrectedAddresses>
        </Fieldset>
        <FieldsetMutedContainer>
          <Fieldset isMuted={true}>
            <Legend>
              <Phrase id="SALES_ADDRESS_CORRECTION.ORIGINAL.TITLE" />
            </Legend>
            <RadioButton
              checked={selectedAddressId === RADIO_CUSTOM_VALUE}
              name={RADIO_GROUP_NAME}
              onChange={handleRadioChange}
              value={RADIO_CUSTOM_VALUE}
            >
              {street1} <br />
              {street2 && (
                <>
                  {street2} <br />
                </>
              )}
              {`${city}, ${state} ${zipCode}`}
            </RadioButton>
          </Fieldset>
          <ContextBubbleCustom
            hasStreet2={!!street2}
            isEnabled={isCustomSelectionErrorVisible}
            pointer={{
              horizontal: 'center',
              vertical: 'bottom',
            }}
          >
            <Phrase id="SALES_ADDRESS_CORRECTION.WARNING" />
          </ContextBubbleCustom>
        </FieldsetMutedContainer>
      </Container>
      <Buttons>
        <CarouselBottomButton
          buttonFormat={bottomButtonFormat}
          carouselItemId={CAROUSEL_ITEM_ID}
          errorMessage={continueButtonErrorMessage}
          isDisabled={isDisabled}
          isLoading={isLoading}
          onContinueClick={handleContinueClick}
        />
      </Buttons>
    </FormGroup>
  );
};
